import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  IconButton,
  Alert,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Bookmark as BookmarkIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useUserSettings, TickerSymbol } from '../contexts/UserSettingsContext';
import { TranslationSettings } from '../components/TranslationSettings';

// Popular trading symbols for autocomplete
const popularSymbols: TickerSymbol[] = [
  // Forex
  { proName: "FX_IDC:EURUSD", title: "EUR/USD" },
  { proName: "FX_IDC:GBPUSD", title: "GBP/USD" },
  { proName: "FX_IDC:USDJPY", title: "USD/JPY" },
  { proName: "FX_IDC:USDCHF", title: "USD/CHF" },
  { proName: "FX_IDC:AUDUSD", title: "AUD/USD" },
  { proName: "FX_IDC:USDCAD", title: "USD/CAD" },
  { proName: "FX_IDC:NZDUSD", title: "NZD/USD" },
  { proName: "FX_IDC:EURGBP", title: "EUR/GBP" },
  { proName: "FX_IDC:EURJPY", title: "EUR/JPY" },
  { proName: "FX_IDC:GBPJPY", title: "GBP/JPY" },
  
  // Indices
  { proName: "FOREXCOM:SPXUSD", title: "S&P 500 Index" },
  { proName: "FOREXCOM:NSXUSD", title: "US 100 Cash CFD" },
  { proName: "FOREXCOM:DJI", title: "Dow Jones" },
  { proName: "FOREXCOM:UKXGBP", title: "UK 100" },
  { proName: "FOREXCOM:FRAXEUR", title: "France 40" },
  { proName: "FOREXCOM:DAXEUR", title: "Germany 40" },
  { proName: "FOREXCOM:JPN225USD", title: "Japan 225" },
  
  // Commodities
  { description: "Gold", proName: "OANDA:XAUUSD" },
  { description: "Silver", proName: "OANDA:XAGUSD" },
  { description: "Oil WTI", proName: "OANDA:WTIUSD" },
  { description: "Oil Brent", proName: "OANDA:BCOUSD" },
  { description: "Natural Gas", proName: "OANDA:NATGASUSD" },
  
  // Crypto
  { proName: "BITSTAMP:BTCUSD", title: "Bitcoin" },
  { proName: "BITSTAMP:ETHUSD", title: "Ethereum" },
  { proName: "BINANCE:ADAUSDT", title: "Cardano" },
  { proName: "BINANCE:BNBUSDT", title: "Binance Coin" },
  { proName: "BINANCE:SOLUSDT", title: "Solana" },
  { proName: "BINANCE:DOTUSDT", title: "Polkadot" },
  { proName: "COINBASE:LINKUSD", title: "Chainlink" },
  
  // Stocks
  { proName: "NASDAQ:AAPL", title: "Apple Inc." },
  { proName: "NASDAQ:GOOGL", title: "Alphabet Inc." },
  { proName: "NASDAQ:MSFT", title: "Microsoft Corp." },
  { proName: "NASDAQ:AMZN", title: "Amazon.com Inc." },
  { proName: "NASDAQ:TSLA", title: "Tesla Inc." },
  { proName: "NASDAQ:NVDA", title: "NVIDIA Corp." },
  { proName: "NYSE:JPM", title: "JPMorgan Chase & Co." },
  { proName: "NYSE:JNJ", title: "Johnson & Johnson" },
];

export const Settings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings, updateTickerSymbols, updateWatchlistSymbols, updateNotificationSettings, loading, error } = useUserSettings();
  
  const [selectedSymbols, setSelectedSymbols] = useState<TickerSymbol[]>(settings.tickerSymbols);
  const [watchlistSymbols, setWatchlistSymbols] = useState<TickerSymbol[]>(settings.watchlistSymbols);
  const [notificationSettings, setNotificationSettings] = useState(settings.notifications);
  const [saving, setSaving] = useState(false);
  const [savingWatchlist, setSavingWatchlist] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [success, setSuccess] = useState(false);
  const [watchlistSuccess, setWatchlistSuccess] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState(false);

  // Update state when settings change
  useEffect(() => {
    setSelectedSymbols(settings.tickerSymbols);
    setWatchlistSymbols(settings.watchlistSymbols);
    setNotificationSettings(settings.notifications);
  }, [settings]);

  const handleAddSymbol = (symbol: TickerSymbol | null) => {
    if (symbol && selectedSymbols.length < 10) {
      // Check if symbol already exists
      const exists = selectedSymbols.some(s => s.proName === symbol.proName);
      if (!exists) {
        setSelectedSymbols([...selectedSymbols, symbol]);
      }
    }
  };

  const handleRemoveSymbol = (index: number) => {
    setSelectedSymbols(selectedSymbols.filter((_, i) => i !== index));
  };

  const handleAddToWatchlist = (symbol: TickerSymbol | null) => {
    if (symbol && watchlistSymbols.length < 20) {
      // Check if symbol already exists
      const exists = watchlistSymbols.some(s => s.proName === symbol.proName);
      if (!exists) {
        setWatchlistSymbols([...watchlistSymbols, symbol]);
      }
    }
  };

  const handleRemoveFromWatchlist = (index: number) => {
    setWatchlistSymbols(watchlistSymbols.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (selectedSymbols.length === 0) {
      return;
    }

    try {
      setSaving(true);
      setSuccess(false);
      await updateTickerSymbols(selectedSymbols);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving ticker symbols:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWatchlist = async () => {
    try {
      setSavingWatchlist(true);
      setWatchlistSuccess(false);
      await updateWatchlistSymbols(watchlistSymbols);
      setWatchlistSuccess(true);
      setTimeout(() => setWatchlistSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving watchlist symbols:', err);
    } finally {
      setSavingWatchlist(false);
    }
  };

  const handleNotificationToggle = (setting: 'inBrowserEnabled' | 'pushEnabled') => {
    setNotificationSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSaveNotifications = async () => {
    try {
      setSavingNotifications(true);
      setNotificationSuccess(false);
      await updateNotificationSettings(notificationSettings);
      setNotificationSuccess(true);
      setTimeout(() => setNotificationSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving notification settings:', err);
    } finally {
      setSavingNotifications(false);
    }
  };

  const getSymbolDisplayName = (symbol: TickerSymbol) => {
    return symbol.title || symbol.description || symbol.proName.split(':')[1] || symbol.proName;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant={isMobile ? 'h4' : 'h2'}
                component="h1"
                gutterBottom
                sx={{ mb: 1 }}
              >
                <SettingsIcon sx={{ fontSize: 'inherit', mr: 2, verticalAlign: 'middle' }} />
                Settings
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '600px', mx: 'auto' }}
              >
                Configure your trading preferences and customize your dashboard experience.
              </Typography>
            </Box>
          </motion.div>

          {/* Ticker Symbols Configuration */}
          <motion.div variants={itemVariants}>
            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    Market Ticker Symbols
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose up to 10 symbols to display in your dashboard ticker. These will show real-time market data.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Ticker symbols updated successfully!
                  </Alert>
                )}

                {/* Current Symbols */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Selected Symbols ({selectedSymbols.length}/10)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {selectedSymbols.map((symbol, index) => (
                      <Chip
                        key={symbol.proName}
                        label={getSymbolDisplayName(symbol)}
                        onDelete={() => handleRemoveSymbol(index)}
                        deleteIcon={<DeleteIcon />}
                        color="primary"
                        variant="filled"
                      />
                    ))}
                  </Box>
                  {selectedSymbols.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No symbols selected. Add some symbols to display market data.
                    </Typography>
                  )}
                </Box>

                {/* Add Symbol */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Add Symbol
                  </Typography>
                  <Autocomplete
                    options={popularSymbols.filter(symbol => 
                      !selectedSymbols.some(s => s.proName === symbol.proName)
                    )}
                    getOptionLabel={(option) => getSymbolDisplayName(option)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {getSymbolDisplayName(option)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.proName}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    onChange={(_, value) => handleAddSymbol(value)}
                    disabled={selectedSymbols.length >= 10}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search and select symbols"
                        placeholder="Type to search for symbols..."
                        helperText={
                          selectedSymbols.length >= 10 
                            ? "Maximum 10 symbols allowed" 
                            : "Search for forex pairs, indices, commodities, crypto, or stocks"
                        }
                      />
                    )}
                  />
                </Box>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || loading || selectedSymbols.length === 0}
                    sx={{ minWidth: 120 }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          {/* Watchlist Configuration */}
          <motion.div variants={itemVariants}>
            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <BookmarkIcon sx={{ mr: 1 }} />
                    Watchlist
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage your watchlist of symbols.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {watchlistSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Watchlist updated successfully!
                  </Alert>
                )}

                {/* Current Watchlist */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Selected Watchlist ({watchlistSymbols.length}/20)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {watchlistSymbols.map((symbol, index) => (
                      <Chip
                        key={symbol.proName}
                        label={getSymbolDisplayName(symbol)}
                        onDelete={() => handleRemoveFromWatchlist(index)}
                        deleteIcon={<DeleteIcon />}
                        color="primary"
                        variant="filled"
                      />
                    ))}
                  </Box>
                  {watchlistSymbols.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No symbols in watchlist. Add some symbols to track market data.
                    </Typography>
                  )}
                </Box>

                {/* Add to Watchlist */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Add Symbol to Watchlist
                  </Typography>
                  <Autocomplete
                    options={popularSymbols.filter(symbol => 
                      !watchlistSymbols.some(s => s.proName === symbol.proName)
                    )}
                    getOptionLabel={(option) => getSymbolDisplayName(option)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {getSymbolDisplayName(option)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.proName}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    onChange={(_, value) => handleAddToWatchlist(value)}
                    disabled={watchlistSymbols.length >= 20}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search and select symbols"
                        placeholder="Type to search for symbols..."
                        helperText={
                          watchlistSymbols.length >= 20 
                            ? "Maximum 20 symbols allowed" 
                            : "Search for forex pairs, indices, commodities, crypto, or stocks"
                        }
                      />
                    )}
                  />
                </Box>

                {/* Save Watchlist Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveWatchlist}
                    disabled={savingWatchlist || loading || watchlistSymbols.length === 0}
                    sx={{ minWidth: 120 }}
                  >
                    {savingWatchlist ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          {/* Translation Settings */}
          <motion.div variants={itemVariants}>
            <TranslationSettings />
          </motion.div>

          {/* Notification Preferences */}
          <motion.div variants={itemVariants}>
            <Card elevation={3} sx={{ mb: 4 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <NotificationsIcon sx={{ mr: 1 }} />
                    Notification Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure how you receive notifications for chat messages and other updates.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {notificationSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Notification preferences updated successfully!
                  </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Notification Types
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationSettings.inBrowserEnabled}
                          onChange={() => handleNotificationToggle('inBrowserEnabled')}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            In-Browser Notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Show notification cards that slide in from the right side of your browser when you receive messages while viewing other chats or pages.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationSettings.pushEnabled}
                          onChange={() => handleNotificationToggle('pushEnabled')}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            System Push Notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Show system notifications (browser popups) when you receive messages. These appear even when the browser is in the background.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Box>

                <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Note:</strong> Both notification types follow smart rules and won't spam you when you're actively viewing the chat where messages are being sent. They only appear when you're viewing other chats or are away from the community page.
                  </Typography>
                </Box>

                {/* Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications || loading}
                    sx={{ minWidth: 120 }}
                  >
                    {savingNotifications ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
}; 